import { useEffect, useState } from 'react';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { checkSlugFormat, SLUG_PROBLEM_MESSAGES } from '@garlic/shared';
import { api } from '../lib/api';
import { Field, Input } from '../ui/primitives';

type Check = { state: 'idle' | 'checking' | 'ok' | 'bad'; message?: string; url?: string };

/** Campo de slug com validação de formato local + checagem de disponibilidade no servidor (com debounce). */
export function SlugField({
  value, onChange, exceptId, serverError, onAvailability,
}: {
  value: string;
  onChange: (v: string) => void;
  exceptId?: string;
  serverError?: string;
  onAvailability?: (ok: boolean) => void;
}) {
  const [check, setCheck] = useState<Check>({ state: 'idle' });

  useEffect(() => {
    if (!value) {
      setCheck({ state: 'idle' });
      onAvailability?.(false);
      return;
    }
    const problem = checkSlugFormat(value);
    if (problem) {
      setCheck({ state: 'bad', message: SLUG_PROBLEM_MESSAGES[problem] });
      onAvailability?.(false);
      return;
    }
    setCheck({ state: 'checking' });
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const r = await api<{ available: boolean; reason?: string; url: string }>(
          `/api/dev/restaurants/slug-check?slug=${encodeURIComponent(value)}${exceptId ? `&except=${exceptId}` : ''}`,
          { signal: ctrl.signal },
        );
        setCheck(r.available ? { state: 'ok', url: r.url } : { state: 'bad', message: r.reason, url: r.url });
        onAvailability?.(r.available);
      } catch {
        if (!ctrl.signal.aborted) setCheck({ state: 'idle' });
      }
    }, 350);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, exceptId]);

  const error = serverError ?? (check.state === 'bad' ? check.message : undefined);
  return (
    <Field
      label="Endereço (slug)"
      error={error}
      hint={
        check.state === 'ok' ? (
          <span className="inline-flex items-center gap-1 text-sprout-600">
            <CheckCircle2 className="size-3.5" /> Disponível: {check.url}
          </span>
        ) : check.state === 'checking' ? (
          <span className="inline-flex items-center gap-1"><Loader2 className="size-3.5 animate-spin" /> Verificando…</span>
        ) : (
          'Letras minúsculas, números e hífen. Ex.: bistro-do-joao'
        )
      }
    >
      {(id, d) => (
        <div className="relative">
          <Input
            id={id}
            aria-describedby={d}
            value={value}
            invalid={!!error}
            autoCapitalize="none"
            spellCheck={false}
            className="pr-10 font-mono"
            onChange={(e) => onChange(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
          />
          {check.state === 'ok' && <CheckCircle2 className="absolute right-3 top-1/2 size-4 -translate-y-1/2 text-sprout-600" aria-hidden />}
          {check.state === 'bad' && <XCircle className="absolute right-3 top-1/2 size-4 -translate-y-1/2 text-danger-600" aria-hidden />}
        </div>
      )}
    </Field>
  );
}
